// React imports.
import React, { useMemo } from "react";

//library imports
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

// App imports.
import { IndexHistory } from '../state'
import * as utility from "../utility";
import ReadingIndexValues from "./ReadingIndexValues";

interface BenefitTargetingComp3Props {
  history: IndexHistory;
}

function BenefitTargetingComp3({ history }: BenefitTargetingComp3Props) {
  // Initialize options with useMemo to avoid recalculation
  const options = useMemo(() => {
    const baseOptions = {
      credits: {
        enabled: false
      },
      chart: {
        type: 'column',
      },
      title: {
        text: history.SelectedIndexString + ' Index'
      },
      xAxis: {
        labels: {
          enabled: true,
        },
        categories: [] as string[],
        crosshair: true
      },
      yAxis: {
        plotLines: [{
          color: '#FF0000',
          width: 2,
          value: 100,
          dashStyle: 'ShortDash',
        }],
        min: 0,
        max: 200,
        title: {
          text: history.SelectedIndexString + ' Index'
        }
      },
      series: [
        {
          name: history.SelectedIndexString + ' Index',
          data: [] as any[],
          showInLegend: false,
        }
      ],
      plotOptions: {
        series: {
          dataLabels: {
            enabled: true
          }
        },
        column: {
          pointPadding: 0.2,
          borderWidth: 0
        }
      },
    };

    let dataArray: any[] = [];

    const regionalAvgIndex = history.IndexCode === utility.IndexCodeEnum.TargetingIndex
      ? (history.RegionIndices && history.RegionIndices[0] ? history.RegionIndices[0].RegAvgBenefitTargetingIndex : 0)
      : (history.RegionIndices && history.RegionIndices[0] ? history.RegionIndices[0].RegAvgBurdenReductionIndex : 0);
    dataArray.push({
      y: +regionalAvgIndex,
      color: 'black'
    });

    if (+regionalAvgIndex > 200) {
      baseOptions.yAxis.max = +regionalAvgIndex + 30;
    }
    baseOptions.xAxis.categories.push('Average for States in Your Region');

    dataArray.push({
      y: history.LatestYearSelectedIndex,
      color: 'grey'
    });
    if (+history.LatestYearSelectedIndex > 200) {
      baseOptions.yAxis.max = +history.LatestYearSelectedIndex + 30;
    }
    baseOptions.xAxis.categories.push(history.GranteeName);

    //get states other than the grantees state
    if (history.RegionIndices) {
      history.RegionIndices
        .filter(gr => gr.GranteeName != history.GranteeName)
        .forEach(yr => {
          let prevHighDataPoint: number = 0;
          let dataPoint = history.IndexCode === utility.IndexCodeEnum.TargetingIndex
            ? yr.BenefitTargetingIndex
            : yr.BurdenReductionIndex;
          dataArray.push(dataPoint);
          baseOptions.xAxis.categories.push(yr.GranteeName);

          //update the y axis if value is too high for 0-200 range normally set
          if (dataPoint > 200 && dataPoint > prevHighDataPoint) {
            baseOptions.yAxis.max = dataPoint + 30;
            prevHighDataPoint = dataPoint;
          }
        });
    }
    baseOptions.series[0].data = dataArray;
    
    return baseOptions;
  }, [history]);

  return (
    <div >
      <h2>{history.SelectedIndexString}  Index – Comparing Your State to Other States in Your Region</h2>
      <br />
      In  FY{history.RegionIndices && history.RegionIndices[0] ? history.RegionIndices[0].Year : ''},
      the {history.SelectedIndexString}  Index Scores for LIHEAP grantees in your OCS Region    ({history.RegionIndices && history.RegionIndices[0] ? history.RegionIndices[0].RegionName : ''})   were:
      <br /><br />


      <table>
        <thead>
          <tr>
            <th>Grant Recipient</th>
            <th>{history.SelectedIndexString}  Index</th>
            <th>Number of Bill Payment Assisted Households</th>
            <th>Number of Bill Payment Assisted Households with Complete Performance Data</th>
            <th>Percentage of Bill Payment Assisted Households Represented by Your Performance Data</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Average for States in Your Region</td>
            <td>
              {history.IndexCode === utility.IndexCodeEnum.TargetingIndex
                ? (history.RegionIndices && history.RegionIndices[0] ? history.RegionIndices[0].RegAvgBenefitTargetingIndex : 0)
                : (history.RegionIndices && history.RegionIndices[0] ? history.RegionIndices[0].RegAvgBurdenReductionIndex : 0)}
            </td>
            <td>N/A</td>
            <td>N/A</td>
            <td>N/A</td>
          </tr>
          <tr>
            <td>{history.GranteeName}</td>
            <td>{history.LatestYearSelectedIndex}</td>
            <td>
              {history.LatestYear && history.LatestYear.HouseholdsAssisted ? history.LatestYear.HouseholdsAssisted.toLocaleString() : '0'}
            </td>
            <td>
              {history.LatestYear && history.LatestYear.HouseholdsCompleteData ? history.LatestYear.HouseholdsCompleteData.toLocaleString() : '0'}
            </td>
            <td>
              {history.LatestYear && history.LatestYear.IndexRepresentsPctString ? history.LatestYear.IndexRepresentsPctString : ''}
            </td>
          </tr>
          {history.RegionIndices && history.RegionIndices.filter(g => g.GranteeName !== history.GranteeName).map(reg => {
            return (
              <tr key={reg.GranteeName}>
                <td>
                  {reg.GranteeName}
                </td>
                <td>
                  {history.IndexCode === utility.IndexCodeEnum.TargetingIndex
                    ? reg.BenefitTargetingIndex : reg.BurdenReductionIndex}
                </td>
                <td>
                  {reg.HouseholdsAssisted ? reg.HouseholdsAssisted.toLocaleString() : '0'}
                </td>
                <td>
                  {reg.HouseholdsCompleteData ? reg.HouseholdsCompleteData.toLocaleString() : '0'}
                </td>
                <td>
                  {reg.IndexRepresentsPctString || ''}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      <br /><br />
      {history.IndexCode === utility.IndexCodeEnum.TargetingIndex
        ? (history.RegionIndices && history.RegionIndices.filter(r => r.GranteeName == history.GranteeName).length > 0 ? history.RegionIndices.filter(r => r.GranteeName == history.GranteeName)[0].BenefitTargetingRegionalCompString : '')
        : (history.RegionIndices && history.RegionIndices.filter(r => r.GranteeName == history.GranteeName).length > 0 ? history.RegionIndices.filter(r => r.GranteeName == history.GranteeName)[0].BurdenReductionRegionalCompString : '')}
      <br /><br />
      <div className="container">
        <div className="row">
          <div className="col-sm-7">
            <HighchartsReact highcharts={Highcharts} options={options} />
          </div>
          <div className="col-sm-5 takeaways">
            <ReadingIndexValues history={history} />
          </div>
        </div>
      </div>

    </div>
  );
}

export default BenefitTargetingComp3;
